import {StyleSheet, css} from 'aphrodite';
import React, {useCallback} from 'react';

import AIModalFileChooser, {AIModalSelectedFile} from 'notebook/ai-tools/AIModalFileChooser';
import {StructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {FONT_PROXIMA_TITLE, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import TextArea from 'ui-library/bs/TextArea';
import {ButtonStyle} from 'ui-library/buttons/Button';
import MultiSelectSearchableDropdown from 'ui-library/dropdowns/MultiSelectSearchableDropdown';
import {ModalButton} from 'ui-library/modals/Modal';

import AIDataEntrySuggestedClarifications, {
  AIDataEntrySuggestedClarificationsState,
  SuggestedClarificationsStateType,
} from './AIDataEntrySuggestedClarifications';

export function getAddFilesAndInstructionsModalButtons({
  handleSubmitPrompt,
  isDisabled,
}: {
  handleSubmitPrompt: () => void;
  isDisabled: boolean;
}): Array<ModalButton> {
  return [
    {
      text: 'Submit',
      style: ButtonStyle.PRIMARY,
      onClick: handleSubmitPrompt,
      disabled: isDisabled,
    },
  ];
}

interface AddFilesAndInstructionsStepProps {
  selectedFiles: Array<AIModalSelectedFile>;
  onSetSelectedFiles: (files: Array<AIModalSelectedFile>) => void;
  promptText: string;
  onSetPromptText: (text: string) => void;
  suggestedClarificationsState: AIDataEntrySuggestedClarificationsState;
  structuredTables: Array<StructuredTable<unknown, unknown>>;
  onUpdateSuggestedClarificationsState: (state: AIDataEntrySuggestedClarificationsState) => void;
  onSuggestClarifications: (
    files: Array<AIModalSelectedFile>,
    selectedStructuredTables: Array<StructuredTable<unknown, unknown>>
  ) => void;
  onClearSuggestedClarifications: () => void;
  selectedTableNames: Array<string>;
  setSelectedTableNames: (tableNames: Array<string>) => void;
  selectedStructuredTables: Array<StructuredTable<unknown, unknown>>;
  errorMessage: string | null;
}

/**
 * First phase of data entry assistant modal, where the user defines the task to perform, including any
 * instructions and context.
 */
export default function AddFilesAndInstructionsStep({
  selectedFiles,
  onSetSelectedFiles,
  promptText,
  onSetPromptText,
  suggestedClarificationsState,
  structuredTables,
  onUpdateSuggestedClarificationsState,
  onSuggestClarifications,
  onClearSuggestedClarifications,
  selectedTableNames,
  setSelectedTableNames,
  selectedStructuredTables,
  errorMessage,
}: AddFilesAndInstructionsStepProps): JSX.Element {
  const handleSetPromptText = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onSetPromptText(e.target.value);
      if (
        e.target.value.length === 0 &&
        suggestedClarificationsState.type === SuggestedClarificationsStateType.INSTRUCTIONS_ONLY
      ) {
        onUpdateSuggestedClarificationsState({type: SuggestedClarificationsStateType.UNSET});
      }
      if (
        e.target.value.length > 0 &&
        suggestedClarificationsState.type === SuggestedClarificationsStateType.UNSET
      ) {
        onUpdateSuggestedClarificationsState({type: SuggestedClarificationsStateType.INSTRUCTIONS_ONLY});
      }
    },
    [onSetPromptText, onUpdateSuggestedClarificationsState, suggestedClarificationsState]
  );

  const handleSetSelectedFiles = useCallback(
    (files: Array<AIModalSelectedFile>) => {
      onSetSelectedFiles(files);
      if (files.length > 0) {
        onSuggestClarifications(files, selectedStructuredTables);
      } else {
        onClearSuggestedClarifications();
      }
    },
    [onSetSelectedFiles, onSuggestClarifications, onClearSuggestedClarifications, selectedStructuredTables]
  );

  const handleSuggestClarifications = useCallback(() => {
    onSuggestClarifications(selectedFiles, selectedStructuredTables);
  }, [onSuggestClarifications, selectedFiles, selectedStructuredTables]);

  const handleSetSelectedTableNames = useCallback(
    (tableNames: Array<string>) => {
      setSelectedTableNames(tableNames);
      const selectedStructuredTables = structuredTables.filter(
        (table) => table.name && tableNames.includes(table.name)
      );
      if (selectedFiles.length > 0) {
        onSuggestClarifications(selectedFiles, selectedStructuredTables);
      }
    },
    [setSelectedTableNames, onSuggestClarifications, selectedFiles, structuredTables]
  );

  return (
    <div>
      <div className={css(styles.firstSectionTitle)}>Select tables</div>
      <MultiSelectSearchableDropdown
        optionDatas={structuredTables.map((table) => ({
          name: table.name,
          value: table.name,
        }))}
        selectedValues={selectedTableNames}
        onSelect={handleSetSelectedTableNames}
        noneSelectedMessage="Pick one or more tables"
      />
      <div className={css(styles.sectionTitle)}>Upload files</div>
      <AIModalFileChooser
        selectedFiles={selectedFiles}
        onSetSelectedFiles={handleSetSelectedFiles}
        messageText="Attach any documents, spreadsheets, presentations, or images:"
      />
      {errorMessage && (
        <div className={css(styles.errorMessage)}>
          <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />
        </div>
      )}
      <div className={css(styles.sectionTitle)}>Confirm example row</div>
      <div className={css(styles.suggestedClarificationsContainer)}>
        <AIDataEntrySuggestedClarifications
          suggestedClarificationsState={suggestedClarificationsState}
          structuredTables={selectedStructuredTables}
          onUpdateSuggestedClarificationsState={onUpdateSuggestedClarificationsState}
          onSuggestClarifications={handleSuggestClarifications}
        />
      </div>
      <div className={css(styles.sectionTitle)}>Add instructions</div>
      <div>
        Include any additional instructions or clarifications to help the AI system understand how to fill the
        table:
      </div>
      <TextArea className={css(styles.textArea)} value={promptText} onChange={handleSetPromptText} />
      <div>
        <span className={css(styles.tip)}>Tip:</span> Notebook contents are also considered, including tables
        and any instructions.
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  firstSectionTitle: {
    ...FONT_PROXIMA_TITLE,
    marginBottom: 4,
  },
  sectionTitle: {
    ...FONT_PROXIMA_TITLE,
    marginTop: 15,
    marginBottom: 4,
  },
  errorMessage: {
    marginTop: 4,
    marginBottom: 4,
  },
  suggestedClarificationsContainer: {
    marginBottom: 8,
  },
  textArea: {
    marginTop: 4,
    marginBottom: 4,
  },
  tip: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
});
